import { useDeferredValue, useMemo, useState } from 'react';
import { InterviewQuestionCard } from '../components/interview-practice';
import { ErrorPage, LoadingPage } from '../components/resource-state';
import { loadInterviewBank, type Manifest } from '../content/data';
import { useResource } from '../content/use-resource';
import { firstIncompleteDayId } from '../progress/progress';
import { useProgress } from '../progress/progress-context';

type AttemptFilter = 'all' | 'not-attempted' | 'attempted';

export function InterviewPage({ manifest }: { manifest: Manifest }) {
  const progress = useProgress();
  const currentDayId = firstIncompleteDayId(manifest, progress);
  const currentWeekNumber = manifest.weeks.find((week) => currentDayId?.startsWith(week.id))?.number;
  const [weekNumber, setWeekNumber] = useState(currentWeekNumber ?? manifest.weeks[0]?.number ?? 0);
  const [attemptFilter, setAttemptFilter] = useState<AttemptFilter>('all');
  const [query, setQuery] = useState('');
  const [visibleCount, setVisibleCount] = useState(20);
  const deferredQuery = useDeferredValue(query.trim().toLowerCase());
  const resource = useResource(loadInterviewBank, []);

  const questions = useMemo(() => {
    if (resource.status !== 'ready') return [];
    return resource.data.questions.filter((question) => {
      const weekMatches = weekNumber === 0 || question.weekNumber === weekNumber;
      const attempted = progress.questionAttempts[question.id]?.attempted ?? false;
      const attemptMatches = attemptFilter === 'all' || (attemptFilter === 'attempted' ? attempted : !attempted);
      const searchMatches = !deferredQuery || question.prompt.toLowerCase().includes(deferredQuery) || question.id.toLowerCase().includes(deferredQuery);
      return weekMatches && attemptMatches && searchMatches;
    });
  }, [attemptFilter, deferredQuery, progress.questionAttempts, resource, weekNumber]);

  if (resource.status === 'loading') return <LoadingPage label="Opening the interview bank" />;
  if (resource.status === 'error') return <ErrorPage error={resource.error} retry={resource.retry} />;

  const attemptedTotal = resource.data.questions.filter((question) => progress.questionAttempts[question.id]?.attempted).length;

  return (
    <main className="wide-page interview-page" id="main-content">
      <header className="page-heading interview-heading">
        <div>
          <h1>Interview practice</h1>
          <p>Answer aloud first. Use the model answer to find a missing idea, example, or failure mode.</p>
        </div>
        <p><strong>{attemptedTotal}</strong> of {resource.data.questions.length} attempted</p>
      </header>

      <form className="question-filters" onSubmit={(event) => event.preventDefault()}>
        <label>
          <span>Week</span>
          <select
            value={weekNumber}
            onChange={(event) => {
              setWeekNumber(Number(event.currentTarget.value));
              setVisibleCount(20);
            }}
          >
            <option value={0}>All loaded weeks</option>
            {manifest.weeks.map((week) => <option value={week.number} key={week.id}>Week {week.number}</option>)}
          </select>
        </label>
        <label>
          <span>Attempt</span>
          <select
            value={attemptFilter}
            onChange={(event) => {
              setAttemptFilter(event.currentTarget.value as AttemptFilter);
              setVisibleCount(20);
            }}
          >
            <option value="all">All questions</option>
            <option value="not-attempted">Not attempted</option>
            <option value="attempted">Attempted</option>
          </select>
        </label>
        <label className="search-field">
          <span>Find a topic or question ID</span>
          <input
            type="search"
            value={query}
            placeholder="Example: cascade or Q017"
            onChange={(event) => {
              setQuery(event.currentTarget.value);
              setVisibleCount(20);
            }}
          />
        </label>
      </form>

      <div className="results-heading" aria-live="polite">
        <p>{questions.length} {questions.length === 1 ? 'question' : 'questions'} match</p>
        <p>Your notes stay in this browser.</p>
      </div>

      {questions.length > 0 ? (
        <div className="interview-results">
          {questions.slice(0, visibleCount).map((question) => (
            <InterviewQuestionCard key={question.id} question={question} compact />
          ))}
        </div>
      ) : (
        <div className="empty-result">
          <h2>No questions match</h2>
          <p>Change the week, attempt filter, or search text.</p>
        </div>
      )}

      {visibleCount < questions.length ? (
        <button className="secondary-button load-more" type="button" onClick={() => setVisibleCount((count) => count + 20)}>
          Show 20 more
        </button>
      ) : null}
    </main>
  );
}
